import React from 'react'
import { Dialog, IconButton } from '@mui/material'
import { AlertCircle, ChevronRight, X } from 'lucide-react'
import Button from '../../ui/Button'
import type { UnresolvedAppendixItem } from '../../../types/codeIndex'
import { formatUnresolvedSummary } from '../../../utils/appendixTable'

type UnresolvedAppendicesDialogProps = {
  items: UnresolvedAppendixItem[] | null
  // Letter and name to show for an appendix key, when the codesheet knows it.
  formatName?: (item: UnresolvedAppendixItem) => string
  onOpenAppendix: (item: UnresolvedAppendixItem) => void
  onClose: () => void
}

// Shown when submitting for review is blocked by appendices whose entries point at code items or
// values that no longer exist. Each appendix can be opened to fix it.
export const UnresolvedAppendicesDialog: React.FC<UnresolvedAppendicesDialogProps> = ({
  items,
  formatName,
  onOpenAppendix,
  onClose,
}) => {
  if (!items) return null

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="unresolved-appendices-title"
      PaperProps={{ sx: { borderRadius: '8px' } }}
    >
      <div className="flex items-start gap-3 border-b border-red-200 bg-red-50 px-5 py-4">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B3261E]" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h2 id="unresolved-appendices-title" className="text-base font-semibold text-gray-900">
            Unable to Submit for Review
          </h2>
          <p className="mt-1 text-sm text-gray-700">
            {items.length === 1 ? 'This appendix points' : 'These appendices point'} at code items or values that no longer exist.
            Choose another value, clear the entry or remove the column, then submit again.
          </p>
        </div>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4 text-[#1D5176]" aria-hidden="true" />
        </IconButton>
      </div>
      <ul aria-label="Incomplete appendices" className="max-h-[320px] overflow-y-auto">
        {items.map(item => {
          const name = formatName?.(item) || item.title || 'Untitled appendix'
          return (
            <li key={item.appendixKey || item.title} className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">{name}</p>
                <p className="text-xs text-[#B3261E]">{formatUnresolvedSummary(item.unresolvedCount)}</p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="bg-[#EEF6FF]"
                aria-label={`Open ${name}`}
                onClick={() => onOpenAppendix(item)}
                rightIcon={<ChevronRight className="h-4 w-4" aria-hidden="true" />}
              >
                Open
              </Button>
            </li>
          )
        })}
      </ul>
      <div className="flex justify-end px-5 py-3">
        <Button type="button" size="sm" variant="secondary" className="bg-[#EEF6FF]" onClick={onClose}>
          Close
        </Button>
      </div>
    </Dialog>
  )
}

export default UnresolvedAppendicesDialog
